// @djsem/shared — Release Campaign Planner (36), Post-Show Recorder (37),
// Library Cleanup (38), Pro SDK (39), Marketplace (35).
//
// Five modules in one file, each clearly delimited. All follow established
// contract + service patterns. No UI or backend routes — those are dependency-reported.

import { SeqCounter } from "../runtime";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 38 — Library Cleanup, Dedup, Metadata Quality
// ============================================================================

export type DJSEMLibraryIssueType =
  | "duplicate_track" | "missing_file" | "broken_ref" | "missing_metadata"
  | "bpm_conflict" | "key_conflict" | "duration_mismatch" | "low_analysis_confidence"
  | "orphaned_stem" | "orphaned_asset" | "rights_warning"
  | "unapproved_package_used" | "unsupported_format" | "stale_import" | "duplicate_playlist";

export type DJSEMLibraryIssueSeverity = "info" | "warning" | "high" | "critical";

export interface DJSEMLibrarySuggestedFix {
  id: string;
  label: string;
  fixType: "merge_duplicates" | "update_metadata" | "relink_file" | "remove_orphan" | "reanalyze" | "approve_package" | "mark_rights_reviewed" | "ignore";
  safeToAutoApply: boolean;
  warnings: string[];
}

export interface DJSEMLibraryIssue {
  id: string;
  projectId?: string;
  issueType: DJSEMLibraryIssueType;
  severity: DJSEMLibraryIssueSeverity;
  resourceType: "track" | "stem" | "asset" | "set" | "package" | "playlist" | "import";
  resourceId: string;
  title: string;
  description: string;
  suggestedFixes: DJSEMLibrarySuggestedFix[];
  status: "open" | "ignored" | "resolved";
  createdAt: string;
}

export interface DJSEMLibraryQualityReport {
  projectId: string;
  totalIssues: number;
  bySeverity: Record<DJSEMLibraryIssueSeverity, number>;
  issues: DJSEMLibraryIssue[];
  scannedAt: string;
}

export interface TrackForScan { id: string; title?: string; artist?: string; durationMs?: number; bpm?: number; key?: string; hash?: string; fileRef?: string; }

export class LibraryQualityService {
  detectDuplicates(tracks: TrackForScan[]): DJSEMLibraryIssue[] {
    const issues: DJSEMLibraryIssue[] = [];
    const byHash = new Map<string, TrackForScan[]>();
    for (const t of tracks) {
      if (!t.hash) continue;
      const bucket = byHash.get(t.hash) ?? [];
      bucket.push(t);
      byHash.set(t.hash, bucket);
    }
    for (const [hash, bucket] of byHash) {
      if (bucket.length < 2) continue;
      issues.push({
        id: `li_${_seq.next()}`, issueType: "duplicate_track", severity: "warning",
        resourceType: "track", resourceId: bucket[0].id,
        title: `Duplicate track: ${bucket[0].title ?? "unknown"}`,
        description: `${bucket.length} tracks share hash ${hash}`,
        suggestedFixes: [{ id: `fix_${_seq.next()}`, label: "Merge duplicates", fixType: "merge_duplicates", safeToAutoApply: false, warnings: ["Review before merging"] }],
        status: "open", createdAt: AT,
      });
    }
    return issues;
  }

  detectMissingFiles(tracks: TrackForScan[]): DJSEMLibraryIssue[] {
    return tracks.filter(t => !t.fileRef).map(t => ({
      id: `li_${_seq.next()}`, issueType: "missing_file", severity: "high",
      resourceType: "track" as const, resourceId: t.id,
      title: `Missing file: ${t.title ?? t.id}`,
      description: "No file reference found",
      suggestedFixes: [{ id: `fix_${_seq.next()}`, label: "Relink file", fixType: "relink_file" as const, safeToAutoApply: false, warnings: [] }],
      status: "open" as const, createdAt: AT,
    }));
  }

  detectMetadataIssues(tracks: TrackForScan[]): DJSEMLibraryIssue[] {
    const issues: DJSEMLibraryIssue[] = [];
    for (const t of tracks) {
      if (!t.title) issues.push({ id: `li_${_seq.next()}`, issueType: "missing_metadata", severity: "warning", resourceType: "track", resourceId: t.id, title: "Missing title", description: "Track has no title", suggestedFixes: [{ id: `fix_${_seq.next()}`, label: "Update metadata", fixType: "update_metadata", safeToAutoApply: false, warnings: [] }], status: "open", createdAt: AT });
      if (!t.bpm && !t.key) issues.push({ id: `li_${_seq.next()}`, issueType: "missing_metadata", severity: "info", resourceType: "track", resourceId: t.id, title: "Missing BPM/key", description: "Track has no BPM or key", suggestedFixes: [{ id: `fix_${_seq.next()}`, label: "Reanalyze", fixType: "reanalyze", safeToAutoApply: false, warnings: [] }], status: "open", createdAt: AT });
    }
    return issues;
  }

  scanProject(projectId: string, tracks: TrackForScan[]): DJSEMLibraryQualityReport {
    const all = [...this.detectDuplicates(tracks), ...this.detectMissingFiles(tracks), ...this.detectMetadataIssues(tracks)];
    const bySeverity: Record<DJSEMLibraryIssueSeverity, number> = { info: 0, warning: 0, high: 0, critical: 0 };
    for (const i of all) bySeverity[i.severity]++;
    return { projectId, totalIssues: all.length, bySeverity, issues: all, scannedAt: AT };
  }
}
